import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChannelTile } from '../components/ChannelTile';
import { DropCard } from '../components/DropCard';
import { ContentCard } from '../components/ContentCard';
import { OriginalsFeed } from '../components/OriginalsFeed';
import { Rail, Slide } from '../components/Rail';
import { trendingDrops, risingCreators, type Channel, type Drop } from '../data/mock';
import { content } from '../data/content';
import { fetchChannels } from '../lib/bff';
import { useCurrency } from '../lib/currency';

/**
 * Explore — the way into the marketplace for users who haven't subscribed yet.
 *
 * Storefront layout (think Amazon's stacked rails): a contained Spotlight for
 * featured/paid placement, then horizontal feeds merchandised by angle —
 * Trending (popular), New, Channels, Rising Creators. Content-dense, sideways
 * scroll, the work doing the selling. Copy stays minimal.
 */
export function Explore() {
  const spotlight = trendingDrops[0];
  const trending = [...trendingDrops].sort((a, b) => b.edition.sold - a.edition.sold);
  const fresh = [...trendingDrops].reverse();

  return (
    <main className="min-h-[100dvh] bg-provny-white text-provny-black dark:bg-provny-ink dark:text-provny-white">
      <Spotlight drop={spotlight} />

      <div className="mx-auto max-w-7xl px-6 pb-20">
        <Rail label="Worth Backing">
          {content.map((item) => (
            <Slide key={item.id} width="w-64">
              <ContentCard item={item} />
            </Slide>
          ))}
        </Rail>

        <Rail label="Trending Now">
          {trending.map((drop) => (
            <Slide key={drop.id} width="w-64">
              <DropCard drop={drop} />
            </Slide>
          ))}
        </Rail>

        <ChannelsRail />

        <Rail label="New Drops">
          {fresh.map((drop) => (
            <Slide key={drop.id} width="w-64">
              <DropCard drop={drop} />
            </Slide>
          ))}
        </Rail>

        <Rail label="Rising Creators">
          {risingCreators.map((creator) => (
            <Slide key={creator.handle} width="w-28">
              <Link
                to={`/@${creator.handle}`}
                className="group flex flex-col items-center gap-2 text-center"
              >
                <img
                  src={creator.avatar}
                  alt={`@${creator.handle}`}
                  loading="lazy"
                  className="h-20 w-20 rounded-full object-cover grayscale transition-all duration-300 group-hover:scale-105 group-hover:grayscale-0"
                />
                <span className="text-sm font-semibold text-provny-black dark:text-white">@{creator.handle}</span>
                <span className="text-xs text-provny-muted dark:text-white/55">{creator.channel}</span>
              </Link>
            </Slide>
          ))}
        </Rail>

        <OriginalsFeed />
      </div>
    </main>
  );
}

/**
 * Channels rail — loads active channels from the Provny BFF (fail-open to mock
 * if the BFF is unreachable). Renders nothing until loaded.
 */
function ChannelsRail() {
  const [channels, setChannels] = useState<Channel[] | null>(null);

  useEffect(() => {
    let active = true;
    fetchChannels().then((res) => {
      if (active) setChannels(res);
    });
    return () => {
      active = false;
    };
  }, []);

  if (!channels || channels.length === 0) return null;

  return (
    <Rail label="Channels">
      {channels.map((channel) => (
        <Slide key={channel.slug} width="w-72">
          <ChannelTile channel={channel} />
        </Slide>
      ))}
    </Rail>
  );
}

/** Contained featured banner — paid/curated placement, reined in (not full-bleed). */
function Spotlight({ drop }: { drop: Drop }) {
  const { format } = useCurrency();
  const soldOut = drop.edition.sold >= drop.edition.total;

  return (
    <section className="mx-auto max-w-7xl px-6 pt-8">
      <Link
        to={`/drop/${drop.id}`}
        className="group relative block aspect-[16/7] w-full overflow-hidden rounded-2xl"
      >
        <img
          src={drop.image}
          alt={drop.title}
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-provny-ink/90 via-provny-ink/30 to-transparent" />

        <span className="absolute left-4 top-4 rounded-full border border-white/25 bg-provny-ink/40 px-2.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-white/80 backdrop-blur">
          Featured
        </span>

        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8">
          <span className="text-xs font-semibold uppercase tracking-wider text-white/70">
            {drop.channel}
          </span>
          <h2 className="mt-1.5 max-w-2xl font-heading text-3xl font-black leading-tight tracking-tight sm:text-4xl">
            {drop.title}
          </h2>
          <div className="mt-3 flex items-center gap-4 text-sm">
            <span className="text-white/80">@{drop.creator}</span>
            <span className="font-mono text-white/70">
              {soldOut
                ? `${drop.edition.total} / ${drop.edition.total} · Sold Out`
                : `${drop.edition.sold} / ${drop.edition.total} · ${format(drop.priceSol)}`}
            </span>
          </div>
          <span className="mt-5 inline-block rounded-xl bg-provny-cyber px-5 py-2.5 text-sm font-semibold text-provny-ink transition group-hover:bg-provny-cyber/90">
            {soldOut ? 'View Drop' : 'Collect Now'}
          </span>
        </div>
      </Link>
    </section>
  );
}
